import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mic2, Play } from 'lucide-react';
import { Button } from '../../ui/button';
import { absUrl, errMsg, mtvSwap, mtvSwapVoices } from '../../../lib/aiStudioApi';
import { hasStems } from './mtvSteps';

const box = 'rounded-xl border border-neutral-800 bg-neutral-900/40 p-5';

/** Swap the singer: the vocal stem is re-sung by one of the owner's cloned voices (melody and timing kept). */
export function VoiceSwapCard({ project, onChanged }) {
  const [voices, setVoices] = useState(null);
  const [err, setErr] = useState('');
  const prev = project.mtv?.swap_preview || {};
  const [vid, setVid] = useState(prev.voice_id || '');
  useEffect(() => { mtvSwapVoices().then((d) => setVoices(d.voices || [])).catch((e) => setErr(errMsg(e))); }, []);
  const stems = hasStems(project);
  const running = project.mtv_job?.status === 'running';
  const job = project.mtv_job || {};
  const swapFailed = job.status === 'failed' && /Swapping the singer/.test(job.step || '');
  const voice = (voices || []).find((v) => v.id === vid);
  const previewed = prev.url && prev.voice_id === vid;
  const swap = (preview) => mtvSwap(project.id, { voice_id: vid, voice_name: voice?.name || '', preview })
    .then(() => { toast.success(preview ? 'Making a 15-second preview' : 'Swapping the singer on the whole song'); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  const own = (v) => v.kind === 'own' || v.kind === 'cloned';
  const groups = [['Your cloned voices', (voices || []).filter(own)], ['Voice library', (voices || []).filter((v) => !own(v))]];
  return (
    <div className={box} data-testid="mtv-swap-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Mic2 size={15} className="text-amber-300" /> Swap the singer <span className="font-normal text-neutral-500">(optional)</span></div>
      <p className="mb-3 text-xs text-neutral-400">Your cloned voice sings the song — the original melody and timing are kept. Hear a free 15-second preview first; the full swap is billed per minute on your own voice plan. For a new genre, make the genre cover first, split its vocals, then swap.</p>
      {!stems && <div className="mb-3 rounded-md border border-dashed border-neutral-700 px-3 py-2 text-[11px] text-neutral-400" data-testid="mtv-swap-needs-stems">Split the vocals of the current soundtrack first.</div>}
      {err ? <div className="text-[11px] text-amber-200/90" data-testid="mtv-swap-error">{err}</div> : (
        <div className="space-y-3">
          <select value={vid} onChange={(e) => setVid(e.target.value)} disabled={!voices} data-testid="mtv-swap-voice-select"
            className="w-full rounded-md border border-neutral-700 bg-neutral-950 px-2 py-2 text-xs text-neutral-200">
            <option value="">{voices ? 'Pick a voice…' : 'Loading voices…'}</option>
            {groups.map(([label, list]) => list.length > 0 && (
              <optgroup key={label} label={label}>{list.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</optgroup>
            ))}
          </select>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={!stems || !vid || running} onClick={() => swap(true)} className="border-neutral-700 text-neutral-200" data-testid="mtv-swap-preview-btn">
              {running ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Play size={13} className="mr-1" />} {!stems ? 'Split vocals first' : !vid ? 'Pick a voice to preview' : <>Preview 15 s · <span className="ml-1 text-emerald-300">free</span></>}
            </Button>
            <Button size="sm" disabled={!stems || !previewed || running} onClick={() => swap(false)} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="mtv-swap-full-btn">
              <Mic2 size={13} className="mr-1" /> Swap the full song
            </Button>
          </div>
          {running && /Swapping the singer/.test(job.step || '') && <div className="text-[11px] text-neutral-400" data-testid="mtv-swap-status">{job.step}…</div>}
          {swapFailed && <div className="rounded-md border border-red-400/30 bg-red-500/5 px-3 py-2 text-[11px] text-red-200" data-testid="mtv-swap-job-error">{job.error}</div>}
          {prev.url && (
            <div className="space-y-1 rounded-lg border border-neutral-800 p-2" data-testid="mtv-swap-preview">
              <div className="text-[11px] text-neutral-300">Preview · sung by {prev.voice || 'your voice'}</div>
              <audio src={absUrl(prev.url)} controls preload="none" className="h-7 w-full" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
